import type { FieldState } from 'final-form';
import { type ChangeEvent, type FC, useCallback, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { createUniqueElementId } from '../../app.util';
import type { CommonFormInputProps, FormGroupProps, FormOption } from './form.types';

const css = stylesheet({
  warningMessage: {
    color: '#d43900',
  },
});

export interface JFormRadioGroupProps extends Omit<CommonFormInputProps, 'disabled'>, FormGroupProps {
  options: FormOption[];
  inline?: boolean;
  onChange?: (selection: string) => void;
  disabled?: boolean;
}

export const JFormRadioGroup: FC<JFormRadioGroupProps> = ({
  validate,
  name,
  options,
  description,
  className,
  label,
  inline,
  onChange,
  disabled,
}) => {
  const inputId = useMemo(() => createUniqueElementId('checkbox'), []);
  const [validateMessage, setValidateMessage] = useState<string | undefined>('');
  const getValidateMessage = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    (value: any, allValues: any, meta?: FieldState<any>) => {
      if (validate) {
        setValidateMessage(validate(value, allValues, meta));
      }
    },
    [
      validate,
    ],
  );
  return (
    <Form.Group className={className}>
      <Form.Label className="d-block">{label && (typeof label === 'string' ? label : label())}</Form.Label>
      {options.map((option, index) => (
        <Field key={option.value} name={name} type="radio" validate={getValidateMessage} value={option.value}>
          {(fieldProps) => (
            <Form.Check
              aria-describedby={description && `${inputId}-description`}
              data-cy={`radio-${name}`}
              feedback={fieldProps.meta.error}
              id={`${inputId}-${index}`}
              inline={inline}
              isInvalid={fieldProps.meta.invalid}
              label={option.text}
              {...fieldProps.input}
              disabled={disabled || option.disabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                fieldProps.input.onChange(event);
                onChange?.(event.target.value);
              }}
              type="radio"
            />
          )}
        </Field>
      ))}
      {validateMessage && (
        <Form.Text className={css.warningMessage} id={`${inputId}-validate`}>
          {validateMessage}
        </Form.Text>
      )}
      {description && (
        <Form.Text id={`${inputId}-description`} muted>
          {typeof description === 'string' ? description : description()}
        </Form.Text>
      )}
    </Form.Group>
  );
};
